import { useMemo } from "react";
import { Surface, Pill, AButton } from "@/components/ios";
import { RoomOverview, RoomEvidencePool, ItemCard, CustomItemCard } from "@/components/InspectionPieces";
import {
  ChevronLeft, ChevronRight, Check, CheckCheck, Plus, Camera, Aperture,
  Sparkles, Loader2, Footprints,
} from "lucide-react";
import { motion } from "framer-motion";

const RATING = ["clean", "undamaged", "working"];

export default function GuidedInspection({
  schema, roomIdx, setRoomIdx, responses, media, dict, h,
}) {
  const sections = schema.sections;
  const section = sections[roomIdx];

  const roomStatus = useMemo(() => sections.map((s) => {
    const done = s.items.filter((it) => responses[it.key] && RATING.some((f) => responses[it.key][f] !== undefined)).length;
    const photos = media.filter((m) => m.room === s.title).length;
    const hasOverview = media.some((m) => m.room === s.title && m.shot_type === "overall" && m.overview_valid !== false);
    return { done, total: s.items.length, photos, hasOverview };
  }), [sections, responses, media]);

  const roomOverview = media.filter((m) => m.room === section.title && !m.item_key && m.shot_type === "overall");
  const roomUnassigned = media.filter((m) => m.room === section.title && !m.item_key && m.shot_type !== "overall");
  const customEntries = Object.entries(responses).filter(([, v]) => v && v.custom && (v.section_key === section.key || v.room === section.title));

  const st = roomStatus[roomIdx];
  const isLast = roomIdx === sections.length - 1;
  const isFirst = roomIdx === 0;

  return (
    <div data-testid="guided-inspection">
      {/* Progress rail */}
      <div className="flex gap-1.5 overflow-x-auto pb-3 -mx-1 px-1" data-testid="guided-room-rail">
        {sections.map((s, i) => {
          const rs = roomStatus[i];
          const active = i === roomIdx;
          const complete = rs.done === rs.total && rs.hasOverview;
          return (
            <button key={s.key} data-testid={`guided-room-${i}`} onClick={() => setRoomIdx(i)}
              className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border"
              style={{
                background: active ? "var(--accent)" : complete ? "var(--success)" : "var(--surface)",
                color: active || complete ? "#fff" : "var(--text)",
                borderColor: active ? "var(--accent)" : complete ? "var(--success)" : "var(--border)",
              }}>
              {complete && !active && <Check size={12} />}
              {s.title}
            </button>
          );
        })}
      </div>

      <motion.div key={roomIdx} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.22 }}>
        {/* Step header */}
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-secondary">Room {roomIdx + 1} of {sections.length}</div>
            <h2 className="text-xl font-bold tracking-tight" style={{ color: "var(--text)" }}>{section.title}</h2>
          </div>
          <div className="flex items-center gap-2">
            <AButton variant="text" testid="guided-walkthrough" onClick={() => h.setWalk(true)}>
              <Camera size={14} className="inline mr-1" /> Camera
            </AButton>
            <Pill tone={st.done === st.total ? "success" : "neutral"} testid="guided-room-progress">{st.done}/{st.total} items</Pill>
            <Pill tone="neutral"><Camera size={11} /> {st.photos}</Pill>
          </div>
        </div>

        {/* Step 1: wide shot */}
        {!st.hasOverview && (
          <Surface className="p-4 mb-3" data-testid="guided-need-overview" style={{ borderColor: "var(--accent)" }}>
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--accent-soft)" }}>
                <Aperture size={20} className="accent-text" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm" style={{ color: "var(--text)" }}>Start with a wide shot</div>
                <p className="text-xs text-secondary mt-0.5">Capture the whole {section.title} from a doorway or corner. The AI checks it's a genuine wide shot (not a close-up) so you have tribunal-ready context.</p>
                <AButton testid="guided-open-walkthrough" className="mt-3" onClick={() => h.setWalk(true)}>
                  <Footprints size={15} className="inline mr-1.5" /> Capture wide shot
                </AButton>
              </div>
            </div>
          </Surface>
        )}

        <div className="space-y-3">
          <RoomOverview roomOverview={roomOverview} section={section} assignMedia={h.assignMedia} />
          <RoomEvidencePool roomUnassigned={roomUnassigned} section={section} matching={h.matching} autoMatch={h.autoMatch}
            acceptCandidate={h.acceptCandidate} dismissCandidate={h.dismissCandidate} assignMedia={h.assignMedia} />

          {/* Step 2 & 3: items + condition */}
          <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
            <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>Items & condition</div>
            <div className="flex items-center gap-3">
              <AButton variant="text" testid="add-item-btn" onClick={() => h.addCustomItem(section)}>
                <Plus size={14} className="inline mr-1" /> Add item
              </AButton>
              <AButton variant="text" testid="mark-room-good" onClick={() => h.markGoodRoom(section)}>
                <CheckCheck size={14} className="inline mr-1" /> Mark room good
              </AButton>
            </div>
          </div>

          {section.items.map((it) => (
            <ItemCard key={it.key} it={it} r={responses[it.key] || {}} itemMedia={media.filter((m) => m.item_key === it.key)}
              room={section.title} dict={dict} setItem={h.setItem} setCam={h.setCam} onPickFile={h.onPickFile}
              unassignMedia={h.unassignMedia} aiCheck={h.aiCheck} assignMedia={h.assignMedia} />
          ))}

          {customEntries.map(([ckey, cr]) => (
            <CustomItemCard key={ckey} ckey={ckey} cr={cr} itemMedia={media.filter((m) => m.item_key === ckey)}
              room={section.title} dict={dict} setItem={h.setItem} setCam={h.setCam} removeCustomItem={h.removeCustomItem} />
          ))}
        </div>
      </motion.div>

      {/* Step nav */}
      <div className="sticky bottom-24 lg:bottom-4 mt-6 flex items-center justify-between gap-3 p-3 rounded-2xl glass border" style={{ borderColor: "var(--border)" }}>
        <AButton variant="text" testid="guided-prev" disabled={isFirst} onClick={() => setRoomIdx((i) => Math.max(0, i - 1))}>
          <ChevronLeft size={16} className="inline" /> Previous
        </AButton>
        {isLast ? (
          <AButton testid="guided-review" disabled={h.checking} onClick={h.generate}>
            {h.checking ? <Loader2 size={16} className="inline mr-1.5 animate-spin" /> : <Sparkles size={16} className="inline mr-1.5" />}
            {h.checking ? "Checking…" : "Review & generate"}
          </AButton>
        ) : (
          <AButton testid="guided-next" onClick={() => setRoomIdx((i) => Math.min(sections.length - 1, i + 1))}>
            Next room <ChevronRight size={16} className="inline" />
          </AButton>
        )}
      </div>
    </div>
  );
}
